import { useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
  ArrowsDownUpIcon,
  AudioIcon,
  DragIcon,
  FileIcon,
  FileTextIcon,
} from 'assets/icons';
import { AlbumContentType } from 'common/enums';
import { moveItem } from 'helpers/memory-order';
import { adaptContentType, classnames } from 'helpers/utils';
import { usePdfFirstPage } from 'hooks/use-pdf-first-page';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import styles from './album-entry-list.module.scss';
import reorderStyles from './album-entry-reorder.module.scss';

interface AlbumEntryListProps {
  contents: AlbumContentSerialized[];
  activeContentId?: number;
  onSelect: (content: AlbumContentSerialized) => void;
  // Owners and collaborators may arrange the list: the panel gets a header
  // with a reorder button, and each drop hands back the whole new order.
  onReorder?: (contents: AlbumContentSerialized[]) => void;
}

// Media-fragment timestamp: tells the browser to paint the frame at t=10s.
// Mirrors the album thumbnail preview (see common/album-thumbnail).
const PREVIEW_TIME_SECONDS = 10;

const iconFor = (type: AlbumContentType) => {
  switch (type) {
    case AlbumContentType.audio:
      return <AudioIcon />;
    case AlbumContentType.write:
      return <FileTextIcon />;
    default:
      return <FileIcon />;
  }
};

// Images, videos and PDFs (their first page) show a real thumbnail;
// everything else shows an icon.
const EntryThumb = ({ content }: { content: AlbumContentSerialized }) => {
  const [videoFailed, setVideoFailed] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const pdfPage = usePdfFirstPage(content, rootRef);
  const type = adaptContentType(content);
  const videoUrl = content.mediaUrl || content.content?.videoUrl || null;

  let media: React.ReactNode = null;
  if (content.thumbnailUrl && type !== AlbumContentType.audio) {
    // Server-generated thumbnail always wins, except an audio recording's
    // (a black frame of its empty picture): audio gets its icon.
    media = (
      <img src={content.thumbnailUrl} alt="" className={styles.thumbImg} />
    );
  } else if (type === AlbumContentType.image && content.mediaUrl) {
    media = <img src={content.mediaUrl} alt="" className={styles.thumbImg} />;
  } else if (type === AlbumContentType.video && videoUrl && !videoFailed) {
    media = (
      <video
        src={`${videoUrl}#t=${PREVIEW_TIME_SECONDS}`}
        className={styles.thumbImg}
        preload="metadata"
        muted
        playsInline
        onError={() => setVideoFailed(true)}
      />
    );
  } else if (pdfPage) {
    media = (
      <img
        src={pdfPage}
        alt=""
        className={classnames(styles.thumbImg, styles.pdfPage)}
      />
    );
  }

  return (
    <span
      ref={rootRef}
      className={classnames(styles.thumb, {
        // A PDF's page sits on the same tile as the file icon.
        [styles.thumbFallback]: !media || !!pdfPage,
      })}
    >
      {media ?? iconFor(type)}
    </span>
  );
};

const memoryCount = (count: number) =>
  `${count} ${count === 1 ? 'memory' : 'memories'}`;

export const AlbumEntryList = ({
  contents,
  activeContentId,
  onSelect,
  onReorder,
}: AlbumEntryListProps) => {
  const [reordering, setReordering] = useState(false);
  const canReorder = !!onReorder && contents.length > 1;

  const header = canReorder && (
    <div className={reorderStyles.header}>
      <span className={reorderStyles.count}>
        {memoryCount(contents.length)}
      </span>
      {reordering ? (
        <button
          type="button"
          className={reorderStyles.done}
          onClick={() => setReordering(false)}
        >
          Done
        </button>
      ) : (
        <button
          type="button"
          className={reorderStyles.iconButton}
          aria-label="Reorder memories"
          title="Reorder memories"
          onClick={() => setReordering(true)}
        >
          <ArrowsDownUpIcon />
        </button>
      )}
    </div>
  );

  if (reordering && onReorder) {
    return (
      <>
        {header}
        <p className={reorderStyles.hint}>
          Drag a memory by its handle. The new order is saved as you go.
        </p>
        <DragDropContext
          onDragEnd={({ source, destination }) => {
            if (!destination || source.index === destination.index) return;
            onReorder(moveItem(contents, source.index, destination.index));
          }}
        >
          <Droppable droppableId="view-mode-memories">
            {(droppable) => (
              <div
                ref={droppable.innerRef}
                className={styles.list}
                {...droppable.droppableProps}
              >
                {contents.map((content, index) => (
                  <Draggable
                    key={content.id}
                    draggableId={String(content.id)}
                    index={index}
                  >
                    {(draggable, snapshot) => (
                      <div
                        ref={draggable.innerRef}
                        className={classnames(
                          styles.item,
                          reorderStyles.reorderItem,
                          { [reorderStyles.dragging]: snapshot.isDragging },
                        )}
                        {...draggable.draggableProps}
                      >
                        <span
                          className={reorderStyles.handle}
                          aria-label={`Move ${content.name || 'memory'}`}
                          {...draggable.dragHandleProps}
                        >
                          <DragIcon />
                        </span>
                        <EntryThumb content={content} />
                        <span className={styles.name}>
                          {content.name || 'Untitled'}
                        </span>
                      </div>
                    )}
                  </Draggable>
                ))}
                {droppable.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </>
    );
  }

  return (
    <>
      {header}
      <div className={styles.list}>
        {contents.map((content) => (
          <button
            key={content.id}
            type="button"
            className={classnames(styles.item, {
              [styles.itemActive]: content.id === activeContentId,
            })}
            onClick={() => onSelect(content)}
          >
            <EntryThumb content={content} />
            <span className={styles.name}>{content.name || 'Untitled'}</span>
          </button>
        ))}
      </div>
    </>
  );
};
